import type { InWorldTime, CampaignCalendar, TimeOfDay } from '@roll1/shared';
import { TIME_OF_DAY_LABELS } from '../lib/time-of-day-theme';
import { Sun, Moon, Sunrise, Sunset } from 'lucide-react';

const TIME_OF_DAY_ICONS: Record<TimeOfDay, typeof Sun> = {
  dawn: Sunrise,
  morning: Sun,
  midday: Sun,
  afternoon: Sun,
  dusk: Sunset,
  evening: Moon,
  night: Moon,
  midnight: Moon,
};

const TIME_OF_DAY_ICON_COLORS: Record<TimeOfDay, string> = {
  dawn: 'text-warn',
  morning: 'text-warn',
  midday: 'text-warn',
  afternoon: 'text-warn',
  dusk: 'text-warn',
  evening: 'text-ai',
  night: 'text-ai',
  midnight: 'text-muted-foreground',
};

function formatDate(cursor: InWorldTime, calendar?: CampaignCalendar): string | null {
  if (cursor.day == null || cursor.month == null) return null;
  const monthName = calendar?.months[cursor.month]?.name ?? `Month ${cursor.month + 1}`;
  const yearStr = cursor.year != null && calendar?.yearLabel
    ? `, ${cursor.year} ${calendar.yearLabel}`
    : cursor.year != null ? `, ${cursor.year}` : '';
  return `${cursor.day} ${monthName}${yearStr}`;
}

interface TimeOfDayHudProps {
  cursor: InWorldTime;
  calendar?: CampaignCalendar;
}

export default function TimeOfDayHud({ cursor, calendar }: TimeOfDayHudProps) {
  const tod = cursor.timeOfDay;
  if (!tod) return null;

  const Icon = TIME_OF_DAY_ICONS[tod];
  const iconColor = TIME_OF_DAY_ICON_COLORS[tod];
  const label = TIME_OF_DAY_LABELS[tod];
  const date = formatDate(cursor, calendar);

  return (
    <div
      className="absolute top-3 right-3 pointer-events-none select-none"
      style={{ zIndex: 'var(--z-sidebar)' }}
    >
      <div className="flex items-center gap-1.5 bg-shade/70 backdrop-blur-sm rounded-lg px-2.5 py-1.5 border border-fg/10">
        <Icon className={`h-4 w-4 shrink-0 ${iconColor}`} />
        <div className="flex flex-col">
          <span className="text-meta font-medium text-fg/90 leading-tight">{label}</span>
          {date && (
            <span className="text-label text-fg/50 leading-tight">{date}</span>
          )}
        </div>
      </div>
    </div>
  );
}
